// THE single source of spatial visual defaults (Global Constraints: "polish defaults are law").
// Every map surface consumes these — NO other file hardcodes a spatial color or size. Selection is
// always a high-contrast halo + a size bump; it NEVER changes a layer's fill color (color encodes
// layer identity). Pure module — no maplibre import — so it is eager-safe and unit-testable.

/**
 * Okabe–Ito colorblind-safe qualitative palette (black omitted). Layers cycle through it; a new
 * layer takes the first unused color. Do not reorder — persisted layer styles reference by value.
 */
export const SPATIAL_LAYER_PALETTE = [
  '#E69F00', // orange
  '#56B4E9', // sky blue
  '#009E73', // bluish green
  '#F0E442', // yellow
  '#0072B2', // blue
  '#D55E00', // vermillion
  '#CC79A7', // reddish purple
] as const;

export const SPATIAL_STYLE_DEFAULTS = {
  pointRadius: 5,
  pointRadiusSelected: 7,
  lineWidth: 2,
  lineWidthSelected: 3,
  polygonFillOpacity: 0.2,
  polygonOutlineWidth: 2,
  // Cluster bubble radii for the <100 / <1000 / ≥1000 buckets. Fill = layer color at 80% opacity;
  // the count label is always white with a dark halo (owned by spatialMapLayers).
  clusterRadii: [12, 16, 22] as const,
} as const;

/** Palette-cycling helper — `layerColorAt(7) === layerColorAt(0)`. */
export function layerColorAt(index: number): string {
  const len = SPATIAL_LAYER_PALETTE.length;
  const i = ((Math.trunc(index) % len) + len) % len;
  return SPATIAL_LAYER_PALETTE[i];
}

// --- color-by category scale (T13/T14) --------------------------------------
// A24/NG11 — the PURE, STABLE category→color scale that "color by column" uses. It lives here (not
// in a surface) so the palette + the overflow colour are the ONE source of spatial colour. Neutral
// grey (still owned here, never hardcoded in a surface) for the 8th+ category so the finite palette
// never wraps into a colour a distinct category already owns.
export const CATEGORY_OTHER_LABEL = 'Other';
export const CATEGORY_OTHER_COLOR = '#9AA0A6';

export type CategoryLegendEntry = { category: string; color: string };
export type CategoryColorScale = {
  /** Ordered legend: sorted distinct categories (≤ palette length) then `Other` when it overflows. */
  legend: CategoryLegendEntry[];
  /** Category label a value maps to (`Other` for overflow / null). Stable for a given input set. */
  categoryOf: (value: unknown) => string;
  /** Colour a value maps to. Stable for a given input set. */
  colorOf: (value: unknown) => string;
};

/**
 * Deterministic map from the SORTED distinct category set of `values` to `SPATIAL_LAYER_PALETTE`,
 * with an `Other` bucket for the 8th-and-beyond category. Pure — same input ⇒ same colours across
 * renders. Null/undefined values fold into `Other` (they never paint their own category).
 */
export function categoryColorScale(values: readonly unknown[]): CategoryColorScale {
  const max = SPATIAL_LAYER_PALETTE.length;
  const distinct = Array.from(
    new Set(values.filter((v) => v !== null && v !== undefined).map((v) => String(v))),
  ).sort();
  const mapped = distinct.slice(0, max);
  const hasOther = distinct.length > max;
  const byCategory = new Map<string, string>();
  mapped.forEach((category, i) => byCategory.set(category, SPATIAL_LAYER_PALETTE[i]));

  const legend: CategoryLegendEntry[] = mapped.map((category) => ({
    category,
    color: byCategory.get(category) as string,
  }));
  if (hasOther) legend.push({ category: CATEGORY_OTHER_LABEL, color: CATEGORY_OTHER_COLOR });

  const categoryOf = (value: unknown): string => {
    if (value === null || value === undefined) return CATEGORY_OTHER_LABEL;
    const key = String(value);
    return byCategory.has(key) ? key : CATEGORY_OTHER_LABEL;
  };
  const colorOf = (value: unknown): string => {
    const category = categoryOf(value);
    return category === CATEGORY_OTHER_LABEL ? CATEGORY_OTHER_COLOR : (byCategory.get(category) as string);
  };

  return { legend, categoryOf, colorOf };
}

// High-contrast selection halo. Near-white on dark theme, near-black on light theme — chosen to
// stand out against any layer color while leaving the fill color untouched.
const HALO_DARK = '#F5F5F5';
const HALO_LIGHT = '#111111';

/**
 * Selection paint = a 2px high-contrast halo (a stroke, never a fill). Returned as MapLibre
 * circle-paint keys; line/fill layers reuse `-stroke-color` for their own selected-outline paint.
 */
export function selectionHaloPaint(dark: boolean): Record<string, unknown> {
  const halo = dark ? HALO_DARK : HALO_LIGHT;
  return {
    'circle-stroke-color': halo,
    'circle-stroke-width': 2,
    'circle-stroke-opacity': 1,
  };
}

// Non-palette mark colors (still defaults that live ONLY here — no other file hardcodes a color).
// `pointStroke` is a subtle outline so light points stay visible on light basemaps; the cluster
// count label is always white with a dark halo (spec); `selectionFill` is transparent so the
// selection halo shows without ever overriding the layer's identity color.
export const SPATIAL_MARK = {
  pointStroke: 'rgba(0,0,0,0.35)',
  selectionFill: 'rgba(0,0,0,0)',
  clusterText: '#FFFFFF',
  clusterTextHalo: '#000000',
} as const;

/** Per-feature label text — theme-aware with a contrasting halo so it stays legible on tiles. */
export function featureLabelColors(dark: boolean): { text: string; halo: string } {
  return dark
    ? { text: '#E5E7EB', halo: 'rgba(0,0,0,0.75)' }
    : { text: '#1F2937', halo: 'rgba(255,255,255,0.9)' };
}

/** App-theme container/canvas background so a style swap never flashes white. */
export function mapBackgroundColor(dark: boolean): string {
  return dark ? '#1b1b1f' : '#f4f5f7';
}

/** Graticule line color for planar mode (theme-aware, low-contrast so features stay legible). */
export function graticuleColor(dark: boolean): string {
  return dark ? '#3a3a42' : '#d4d6dc';
}

// --- T28 map micro-interactions (ghost geometry paste/drop + hold-M measure) -------------------
// Both are transient, non-data overlays — deliberately NOT palette colors (those encode real
// layer identity) so a ghost/measure never gets confused with an actual result-set layer.

/** Ghost (pasted/dropped reference) geometry — a translucent, dashed, non-interactive overlay. */
export const GHOST_STYLE = {
  color: '#6B7280', // slate-gray — reads as "reference", not "data"
  fillOpacity: 0.4,
  lineWidth: 2,
  lineDasharray: [2, 2] as const,
} as const;

/** Hold-M measure line + label — a high-visibility, obviously-transient interaction color. */
export const MEASURE_STYLE = {
  color: '#F59E0B', // amber — distinct from the palette's orange (#E69F00) and every data hue
  lineWidth: 2,
  lineDasharray: [6, 3] as const,
} as const;

/** Draw-a-polygon "in area" filter overlay — an active-selection blue, distinct from the palette
 *  (data), ghost (slate reference) and measure (amber). A solid outline + vertex dots for the
 *  committed ring, a dashed rubber-band to the cursor. Transient: it never encodes real data. */
export const DRAW_STYLE = {
  color: '#2563EB', // blue-600 — reads as "you are selecting", not a data layer
  fillOpacity: 0.15,
  lineWidth: 2,
  rubberDasharray: [4, 3] as const,
  vertexRadius: 4,
} as const;
